import { ErrorType } from '../content';

export enum PollingStatus {
  READY = 'READY',
  PENDING = 'PENDING',
  FAILURE = 'FAILURE',
}

export type PollingResponse = {
  status: PollingStatus;
  error_code?: ErrorType;
};

/**
 * Starts polling a given endpoint at a specified interval to check the status of a job.
 * Calls setAsReady when the job is ready, or setError if the job fails or an error occurs.
 * Returns a cleanup function to stop polling.
 *
 * @param {Object} params - The polling configuration.
 * @param {string} params.endpoint - The endpoint to poll.
 * @param {() => void} params.setAsReady - Callback to invoke when status is READY.
 * @param {(value?: ErrorType) => void} params.setError - Callback to invoke on FAILURE or error.
 * @param {number} params.pollingInterval - Polling interval in milliseconds.
 * @param {string} [params.jobId] - Optional job ID to append as a query parameter.
 * @returns {() => void} Cleanup function to stop polling.
 */
export const pollStatus = ({
  endpoint,
  setAsReady,
  setError,
  pollingInterval,
  jobId,
}: {
  endpoint: string;
  setAsReady: () => void;
  setError: (value?: ErrorType) => void;
  pollingInterval: number;
  jobId?: string;
}) => {
  let timeoutObj: number;
  let isMounted = true;

  const fetcher = async () => {
    try {
      const url = `${endpoint}${jobId ? `?job_id=${jobId}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      if (!isMounted) return;
      if (data?.status === PollingStatus.READY) {
        setAsReady();
      }
      if (data?.status === PollingStatus.FAILURE) {
        setError(data?.error_code || 'DEFAULT');
      }
    } catch (err) {
      if (!isMounted) return;
      setError('DEFAULT');
    }
    if (!isMounted) return;
    // setTimeout's type is different in Node.js and browser, so we cast it to number
    timeoutObj = setTimeout(() => fetcher(), pollingInterval) as unknown as number;
  };
  fetcher();
  return () => {
    isMounted = false;
    clearTimeout(timeoutObj);
  };
};
